import React, { ChangeEvent, FormEvent, useEffect, useMemo, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { toast } from "react-toastify";
import LayoutSubmaster from "../../components/layoutSubmasterConfig/layoutSubmaster";
import LocalStorage from "../../helpers/localstorage";
import { apiClientSubmaster } from "../../services/setupClientSubmaster/apiClientSubmaster";
import {
  downloadBlob,
  formatDocumentDateForFileName,
  formatDateForFileName,
  runPdfOnlyBackup,
  sanitizeFileName,
  sanitizeZipPathSegment,
} from "../../helpers/backupExport";
import { formatDeliveryReportLabel } from "../../helpers/deliveryReport";
import { summarizeDocumentsByEmployee } from "../../helpers/reportDocuments";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";
import "bootstrap-icons/font/bootstrap-icons.css";

interface Employee {
  id: number;
  name: string;
  cpf: string;
  email: string;
}

interface CompanySnapshot {
  id?: number;
  name_company?: string;
  cnpj?: string;
  image?: string;
}

interface DataRow {
  id: number;
  subMainImageUrl: string;
  submasterId: number;
  epirecordId: number;
  createdAt: string;
  updatedAt: string;
  employeesSubmasterId: number;
  receivedsignature: string;
  receivedbiometric: string;
  pdfdocumentregistered: string | null;
  delivery_type?: string | null;
  delivery_reason?: string | null;
  employeesSubmaster: Employee;
  submaster_company?: CompanySnapshot | null;
}

const formatCpf = (cpf: string) => {
  const numbers = cpf.replace(/\D/g, "");
  if (numbers.length !== 11) return cpf;
  return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
};

const formatDate = (date: string) =>
  new Date(date).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

const getLocalDateFilterValue = (date: string) => {
  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) return "";

  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const sortDocumentsAlphabetically = (rows: DataRow[]) =>
  [...rows].sort((firstDocument, secondDocument) =>
    firstDocument.employeesSubmaster.name.localeCompare(
      secondDocument.employeesSubmaster.name,
      "pt-BR",
      {
        sensitivity: "base",
        ignorePunctuation: true,
      }
    )
  );

const buildEmployeeFolderName = (employee: Employee) =>
  sanitizeZipPathSegment(`${employee.name} - CPF ${formatCpf(employee.cpf)}`);

const buildCompanyFolderName = (
  companyName: string | null | undefined,
  fallbackCompanyName: string
) => sanitizeZipPathSegment(companyName || fallbackCompanyName || "Empresa");

function ConfigReportDocument() {
  const storage = useMemo(() => new LocalStorage(), []);
  const authToken = useMemo(() => String(storage.getAuthToken() || ""), [storage]);
  const submasterId = useMemo(() => Number(storage.getId()), [storage]);
  const baseCompanyName = useMemo(() => storage.getName() || "Empresa", [storage]);
  const apiClient = useMemo(() => apiClientSubmaster(authToken), [authToken]);

  const [data, setData] = useState<DataRow[]>([]);
  const [allDocuments, setAllDocuments] = useState<DataRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const [filters, setFilters] = useState({
    name: "",
    cpf: "",
    startDate: "",
    endDate: "",
  });
  const [invalidDateFilters, setInvalidDateFilters] = useState({
    startDate: false,
    endDate: false,
  });
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupFailures, setBackupFailures] = useState<
    { label: string; reason: string }[]
  >([]);
  const [deletePermissionEnabled, setDeletePermissionEnabled] = useState(true);
  const [deliveryTypeReasonEnabled, setDeliveryTypeReasonEnabled] = useState(false);
  const [fallbackCompanyName, setFallbackCompanyName] = useState(baseCompanyName);

  const getEmployeeDocumentHistory = (employeesSubmasterId: number) =>
    [...allDocuments]
      .filter((item) => item.employeesSubmasterId === employeesSubmasterId)
      .sort((firstDocument, secondDocument) => {
        const firstTime = new Date(firstDocument.createdAt).getTime();
        const secondTime = new Date(secondDocument.createdAt).getTime();

        if (firstTime !== secondTime) {
          return firstTime - secondTime;
        }

        return firstDocument.id - secondDocument.id;
      });

  const handleFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value, validity } = event.currentTarget;

    if (name === "startDate" || name === "endDate") {
      setInvalidDateFilters((previous) => ({
        ...previous,
        [name]: validity.badInput || validity.typeMismatch,
      }));
    }

    setFilters((previous) => ({ ...previous, [name]: value }));
    setResetPaginationToggle((previous) => !previous);
  };

  const handleDateInput = (event: FormEvent<HTMLInputElement>) => {
    const { name, validity } = event.currentTarget;

    if (name === "startDate" || name === "endDate") {
      setInvalidDateFilters((previous) => ({
        ...previous,
        [name]: validity.badInput || validity.typeMismatch,
      }));
    }
  };

  const handleCpfChange = (event: ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value.replace(/\D/g, "");
    if (value.length > 11) value = value.slice(0, 11);
    value = value.replace(/(\d{3})(\d)/, "$1.$2");
    value = value.replace(/(\d{3})(\d)/, "$1.$2");
    value = value.replace(/(\d{3})(\d{1,2})$/, "$1-$2");
    setFilters((previous) => ({ ...previous, cpf: value }));
    setResetPaginationToggle((previous) => !previous);
  };

  const clearFilters = () => {
    setFilters({ name: "", cpf: "", startDate: "", endDate: "" });
    setInvalidDateFilters({ startDate: false, endDate: false });
    setResetPaginationToggle((previous) => !previous);
  };

  const hasInvalidDateRange =
    invalidDateFilters.startDate ||
    invalidDateFilters.endDate ||
    (!!filters.startDate &&
      !!filters.endDate &&
      filters.startDate > filters.endDate);

  const filteredData = useMemo(() => {
    if (hasInvalidDateRange) return [];

    const matchingDocuments = allDocuments.filter((item) => {
      const nameMatch = item.employeesSubmaster.name
        .toLowerCase()
        .includes(filters.name.toLowerCase());
      const cpfMatch = formatCpf(item.employeesSubmaster.cpf).includes(filters.cpf);
      const documentDate = getLocalDateFilterValue(item.createdAt);
      const startDateMatch =
        !filters.startDate || (!!documentDate && documentDate >= filters.startDate);
      const endDateMatch =
        !filters.endDate || (!!documentDate && documentDate <= filters.endDate);

      return nameMatch && cpfMatch && startDateMatch && endDateMatch;
    });

    return sortDocumentsAlphabetically(
      summarizeDocumentsByEmployee(matchingDocuments)
    );
  }, [allDocuments, filters, hasInvalidDateRange]);

  const reportsAvailable = filteredData.filter(
    (item) => !!item.pdfdocumentregistered
  ).length;
  const pendingReports = filteredData.length - reportsAvailable;

  const closePreviewModal = () => {
    setShowPreviewModal(false);
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const fetchExportPdf = async (
    employeesSubmasterId: number,
    reportStartDate = filters.startDate,
    reportEndDate = filters.endDate
  ): Promise<Blob> => {
    const response = await apiClient.post(
      `/exportConjuntoPdf`,
      {
        id: employeesSubmasterId,
        submasterId,
        ...(reportStartDate ? { reportStartDate } : {}),
        ...(reportEndDate ? { reportEndDate } : {}),
      },
      { responseType: "blob" }
    );
    return new Blob([response.data], { type: "application/pdf" });
  };

  const fetchDtemittion = async (employeesSubmasterId: number): Promise<string> => {
    const response = await apiClient.post(
      `/crud/get/dtemition/${employeesSubmasterId}/employeesSubmasterId`
    );
    const record = response.data[0];
    return record?.dtemittion?.substring(0, 10) || "";
  };

  const handlePreview = async (employeesSubmasterId: number) => {
    try {
      const blob = await fetchExportPdf(employeesSubmasterId);
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
      setShowPreviewModal(true);
    } catch (error) {
      console.error("Erro ao visualizar PDF:", error);
      toast.error("Erro ao visualizar PDF.");
    }
  };

  const handleDownloadDirect = async (employeesSubmasterId: number) => {
    try {
      const blob = await fetchExportPdf(employeesSubmasterId);
      downloadBlob(blob, "declaracao-recebimento.pdf");
    } catch (error) {
      console.error("Erro ao baixar PDF:", error);
      toast.error("Erro ao baixar PDF.");
    }
  };

  const handleUpdateData = async (employeesSubmasterId: number) => {
    try {
      const dtemittion = await fetchDtemittion(employeesSubmasterId);
      setSelectedDate(dtemittion);
      setSelectedId(employeesSubmasterId);
      (window as any).bootstrap.Modal.getOrCreateInstance(
        document.getElementById("dtemittionModal")
      ).show();
    } catch (error) {
      console.error("Erro ao buscar a data:", error);
      toast.error("Erro ao buscar a data de emissão.");
    }
  };

  const handleBackup = async () => {
    if (backupLoading) return;

    if (filteredData.length === 0) {
      toast.info("Não há registros para incluir no backup.");
      return;
    }

    setBackupLoading(true);
    setBackupFailures([]);
    const dateSuffix = formatDateForFileName(new Date());
    const toastId = toast.info(`Gerando backup... 0/${filteredData.length}`, {
      position: "top-right",
      autoClose: false,
      closeOnClick: false,
      draggable: false,
      closeButton: false,
    });

    try {
      const summary = await runPdfOnlyBackup<DataRow>({
        items: filteredData,
        zipRootName: `backup_relatorios_${dateSuffix}`,
        concurrency: 3,
        getLabel: (row) => row.employeesSubmaster.name,
        onProgress: (done, total) => {
          toast.update(toastId, { render: `Gerando backup... ${done}/${total}` });
        },
        processItem: async (row) => {
          const employeeHistory = getEmployeeDocumentHistory(row.employeesSubmasterId);
          const hasDateFilter = !!filters.startDate || !!filters.endDate;
          const originalDocument = hasDateFilter ? row : employeeHistory[0] ?? row;
          const folderPath = [
            buildCompanyFolderName(
              originalDocument.submaster_company?.name_company,
              fallbackCompanyName
            ),
            buildEmployeeFolderName(row.employeesSubmaster),
          ];

          if (employeeHistory.length === 0) {
            return { pdf: null, folderPath };
          }

          const blob = await fetchExportPdf(
            row.employeesSubmasterId,
            filters.startDate,
            filters.endDate
          );
          const fileName = `${sanitizeFileName(
            `relatorio_conjunto_epi_${formatDocumentDateForFileName(
              originalDocument.createdAt || row.createdAt
            )}_${row.employeesSubmasterId}`
          )}.pdf`;

          return { pdf: { blob, fileName }, folderPath };
        },
      });

      downloadBlob(summary.blob, summary.zipFileName);

      const hasFailures = summary.failedItems.length > 0;
      toast.update(toastId, {
        render: hasFailures
          ? `Backup concluído com ${summary.failedItems.length} falha(s). ${summary.pdfCount} PDFs incluídos.`
          : `Backup concluído! ${summary.pdfCount} PDFs incluídos, ${summary.skippedCount} sem PDF disponível.`,
        type: hasFailures ? "warning" : "success",
        isLoading: false,
        autoClose: 5000,
      });

      if (hasFailures) {
        setBackupFailures(summary.failedItems);
      }
    } catch (error) {
      console.error("Erro ao gerar backup:", error);
      toast.update(toastId, {
        render: "Erro ao gerar o backup. Tente novamente.",
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      setBackupLoading(false);
    }
  };

  const handleDeleteEmployeeDocuments = async (row: DataRow) => {
    if (!deletePermissionEnabled) {
      toast.warning("Ative a permissão de exclusão para remover os dados deste relatório.");
      return;
    }

    const confirmed = window.confirm(
      `Atenção: todos os dados deste funcionário serão apagados permanentemente nesta tela de relatório.

Funcionário: ${row.employeesSubmaster.name}
CPF: ${formatCpf(
        row.employeesSubmaster.cpf
      )}\n\nDeseja continuar?`
    );

    if (!confirmed) {
      return;
    }

    const employeeDocuments = allDocuments.filter(
      (item) => item.employeesSubmasterId === row.employeesSubmasterId
    );

    if (employeeDocuments.length === 0) {
      toast.info("Nenhum documento encontrado para excluir.");
      return;
    }

    try {
      await Promise.all(
        employeeDocuments.map(async (document) => {
          const response = await apiClient.post(
            `/crud/delete/EpicontrolEmployeesSubmaster/${document.id}/${submasterId}`
          );

          if (!response?.data?.res) {
            throw new Error(response?.data?.message || "Falha ao excluir o documento.");
          }
        })
      );

      setAllDocuments((previous) =>
        previous.filter((item) => item.employeesSubmasterId !== row.employeesSubmasterId)
      );
      setData((previous) =>
        previous.filter((item) => item.employeesSubmasterId !== row.employeesSubmasterId)
      );
      toast.success("Dados do relatório excluídos com sucesso.");
    } catch (error) {
      console.error("Erro ao excluir dados do relatório:", error);
      toast.error("Erro ao excluir os dados do relatório.");
    }
  };

  const handleSaveDtemittion = async () => {
    if (!selectedId || !selectedDate) return;

    try {
      await apiClient.post(`/crud/insertOrUpdate/dtemition`, {
        employeesSubmasterId: selectedId,
        submasterId,
        dtemittion: new Date(selectedDate).toISOString(),
      });
      toast.success("Data de emissão atualizada!");
      (window as any).bootstrap.Modal.getOrCreateInstance(
        document.getElementById("dtemittionModal")
      ).hide();
    } catch (error) {
      console.error("Erro ao salvar data:", error);
      toast.error("Erro ao salvar data de emissão.");
    }
  };

  const columns: TableColumn<DataRow>[] = [
    ...(deliveryTypeReasonEnabled ? [{
      name: "Tipo / motivo",
      cell: (row) => (
        <span>{formatDeliveryReportLabel(row.delivery_type, row.delivery_reason)}</span>
      ),
      grow: 1.2,
    } as TableColumn<DataRow>] : []),
    {
      name: "Funcionário",
      selector: (row) => row.employeesSubmaster.name,
      sortable: true,
      grow: 1.45,
      cell: (row) => (
        <div className="report-person">
          <div className="report-person__avatar">
            {row.employeesSubmaster.name.charAt(0).toUpperCase()}
          </div>
          <div className="report-person__meta">
            <span className="report-person__name">{row.employeesSubmaster.name}</span>
            <small>{row.employeesSubmaster.email}</small>
          </div>
        </div>
      ),
    },
    {
      name: "CPF",
      selector: (row) => row.employeesSubmaster.cpf,
      sortable: true,
      cell: (row) => (
        <span className="pill-badge report-pill-neutral">
          {formatCpf(row.employeesSubmaster.cpf)}
        </span>
      ),
    },
    {
      name: "Gerado em",
      selector: (row) => row.createdAt,
      sortable: true,
      cell: (row) => (
        <div className="report-date">
          <i className="bi bi-calendar3" />
          <span>{formatDate(row.createdAt)}</span>
        </div>
      ),
    },
    {
      name: "Status",
      cell: (row) => (
        <span
          className={`pill-badge ${
            row.pdfdocumentregistered ? "report-pill-success" : "report-pill-warning"
          }`}
        >
          <i
            className={`bi ${
              row.pdfdocumentregistered ? "bi-check2-circle" : "bi-hourglass-split"
            } me-1`}
          />
          {row.pdfdocumentregistered ? "PDF disponível" : "Pendente"}
        </span>
      ),
      grow: 1.05,
    },
    {
      name: "Relatório",
      cell: (row) => (
        <div className="report-actions">
          <button
            className={`btn btn-sm report-action-btn ${
              row.pdfdocumentregistered ? "btn-outline-primary" : "btn-outline-secondary"
            }`}
            onClick={() => handlePreview(row.employeesSubmasterId)}
            disabled={!row.pdfdocumentregistered}
            title={row.pdfdocumentregistered ? "Pré-visualizar PDF" : "Nenhum PDF disponível"}
          >
            <i className="bi bi-eye" />
          </button>

          <button
            className={`btn btn-sm report-action-btn ${
              row.pdfdocumentregistered ? "btn-outline-primary" : "btn-outline-secondary"
            }`}
            onClick={() => handleDownloadDirect(row.employeesSubmasterId)}
            disabled={!row.pdfdocumentregistered}
            title={row.pdfdocumentregistered ? "Baixar PDF" : "Nenhum PDF disponível"}
          >
            <i className="bi bi-download" />
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      grow: 1.25,
    },
    {
      name: "Dt. emissão",
      cell: (row) => (
        <div className="report-actions">
          <button
            className="btn btn-sm report-action-btn btn-outline-primary"
            onClick={() => handleUpdateData(row.employeesSubmasterId)}
            title="Editar data de emissão"
          >
            <i className="bi bi-calendar-event" />
          </button>
          {deletePermissionEnabled && (
          <button
            className="btn btn-sm report-action-btn btn-outline-danger"
            onClick={() => handleDeleteEmployeeDocuments(row)}
            title={
              deletePermissionEnabled
                ? "Excluir dados do relatório"
                : "Ative a permissão para excluir"
            }
          >
            <i className="bi bi-trash" />
          </button>
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      grow: 0.85,
    },
  ];

  useEffect(() => {
    const loadDocuments = async () => {
      setLoading(true);
      try {
        const [documentsResponse, submasterResponse, permissionsResponse] = await Promise.all([
          apiClient.post(`/crud/getDocuments/EpicontrolEmployeesSubmaster/${submasterId}`),
          apiClient.post(`/crud/getdata/Submaster/${submasterId}/true`),
          apiClient.post("/reportPermissions", {}),
        ]);

        const submasterData = Array.isArray(submasterResponse.data)
          ? submasterResponse.data[0]
          : submasterResponse.data;

        setDeletePermissionEnabled(submasterData?.delete_permission_enabled !== false);
        setDeliveryTypeReasonEnabled(
          permissionsResponse?.data?.delivery_type_reason_permission_enabled === true
        );
        setFallbackCompanyName(
            submasterData?.fantasy_name ||
            submasterData?.name ||
            baseCompanyName ||
            "Empresa"
        );

        const uniqueData = summarizeDocumentsByEmployee(
          documentsResponse.data as DataRow[]
        );

        setAllDocuments(documentsResponse.data as DataRow[]);
        setData(sortDocumentsAlphabetically(uniqueData));
      } catch (error) {
        console.error("Erro ao buscar os documentos:", error);
        toast.error("Erro ao buscar os documentos.");
      } finally {
        setLoading(false);
      }
    };

    loadDocuments();
  }, [apiClient, baseCompanyName, submasterId]);

  const noDataComponent = (
    <div className="report-empty-state">
      <div className="report-empty-state__icon">
        <i className="bi bi-file-earmark-text" />
      </div>
      <h4>Nenhum relatório encontrado</h4>
      <p>
        Ajuste os filtros ou aguarde novos documentos gerados para visualizar os
        registros aqui.
      </p>
      <button type="button" className="btn btn-outline-primary" onClick={clearFilters}>
        Limpar filtros
      </button>
    </div>
  );

  return (
    <LayoutSubmaster>
      <div className="report-config-page">
        <section className="dashboard-hero report-hero mb-4">
          <div className="report-hero__content">
            <span className="pill-badge">Relatórios</span>
            <h1 className="dashboard-title">Relatório conjunto de recebimento</h1>
            <p className="dashboard-subtitle">
              Consulte os relatórios emitidos, visualize os PDFs e atualize a data de emissão quando necessário.
            </p>
          </div>
          <div className="report-hero__badge">
            <i className="bi bi-file-earmark-richtext" />
            <div>
              <span>Painel de documentos</span>
              <strong>{filteredData.length} registros ativos</strong>
            </div>
          </div>
        </section>

        <div className="row g-3 mb-4">
          <div className="col-md-4">
            <article className="dashboard-card metric-card soft report-metric-card">
              <div className="metric-card__icon">
                <i className="bi bi-folder2-open fs-4 text-primary" />
              </div>
              <div>
                <p className="metric-card__eyebrow">Registros</p>
                <h2 className="metric-card__value">{data.length}</h2>
                <p className="metric-card__caption">Funcionários com documento listado</p>
              </div>
            </article>
          </div>

          <div className="col-md-4">
            <article className="dashboard-card metric-card soft report-metric-card">
              <div className="metric-card__icon">
                <i className="bi bi-file-earmark-pdf fs-4 text-primary" />
              </div>
              <div>
                <p className="metric-card__eyebrow">PDFs disponíveis</p>
                <h2 className="metric-card__value">{reportsAvailable}</h2>
                <p className="metric-card__caption">Prontos para visualização ou download</p>
              </div>
            </article>
          </div>

          <div className="col-md-4">
            <article className="dashboard-card metric-card soft report-metric-card">
              <div className="metric-card__icon">
                <i className="bi bi-hourglass-split fs-4 text-primary" />
              </div>
              <div>
                <p className="metric-card__eyebrow">Pendentes</p>
                <h2 className="metric-card__value">{pendingReports}</h2>
                <p className="metric-card__caption">Ainda sem PDF concluído na consulta atual</p>
              </div>
            </article>
          </div>
        </div>

        <section className="section-panel report-filter-panel mb-4">
          <div className="section-heading">
            <div>
              <h3>Filtros da consulta</h3>
              <p>Busque o relatório por funcionário, CPF ou período de geração.</p>
            </div>
            <button
              type="button"
              className="btn btn-outline-secondary report-clear-btn"
              onClick={clearFilters}
            >
              <i className="bi bi-arrow-counterclockwise me-1" />
              Limpar pesquisa
            </button>
          </div>

          <div className="row g-3 align-items-end">
            <div className="col-lg-3 col-md-6">
              <label className="form-label">Nome do funcionário</label>
              <div className="input-group report-input">
                <span className="input-group-text bg-white">
                  <i className="bi bi-person text-primary" />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Digite o nome"
                  name="name"
                  value={filters.name}
                  onChange={handleFilterChange}
                />
              </div>
            </div>

            <div className="col-lg-3 col-md-6">
              <label className="form-label">CPF</label>
              <div className="input-group report-input">
                <span className="input-group-text bg-white">
                  <i className="bi bi-card-text text-primary" />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="000.000.000-00"
                  name="cpf"
                  value={filters.cpf}
                  onChange={handleCpfChange}
                />
              </div>
            </div>

            <div className="col-lg-3 col-md-6">
              <label className="form-label">Data inicial</label>
              <div className="input-group report-input">
                <span className="input-group-text bg-white">
                  <i className="bi bi-calendar3 text-primary" />
                </span>
                <input
                  type="date"
                  className={`form-control ${
                    invalidDateFilters.startDate ? "is-invalid" : ""
                  }`}
                  name="startDate"
                  value={filters.startDate}
                  max={filters.endDate || undefined}
                  onChange={handleFilterChange}
                  onInput={handleDateInput}
                  aria-invalid={invalidDateFilters.startDate}
                />
              </div>
              {invalidDateFilters.startDate && (
                <div className="invalid-feedback d-block">Informe uma data válida.</div>
              )}
            </div>

            <div className="col-lg-3 col-md-6">
              <label className="form-label">Data final</label>
              <div className="input-group report-input">
                <span className="input-group-text bg-white">
                  <i className="bi bi-calendar3 text-primary" />
                </span>
                <input
                  type="date"
                  className={`form-control ${
                    invalidDateFilters.endDate ? "is-invalid" : ""
                  }`}
                  name="endDate"
                  value={filters.endDate}
                  min={filters.startDate || undefined}
                  onChange={handleFilterChange}
                  onInput={handleDateInput}
                  aria-invalid={invalidDateFilters.endDate}
                />
              </div>
              {invalidDateFilters.endDate && (
                <div className="invalid-feedback d-block">Informe uma data válida.</div>
              )}
            </div>
          </div>
        </section>

        <section className="section-panel report-table-panel">
          <div className="section-heading">
            <div>
              <h3>Documentos emitidos</h3>
              <p>Visualize, baixe ou ajuste a emissão em uma tabela mais clara.</p>
            </div>
            <div className="report-table-panel__summary">
              <span className="pill-badge report-pill-success">
                {reportsAvailable} disponíveis
              </span>
              <span className="pill-badge report-pill-warning">
                {pendingReports} pendentes
              </span>
              <button
                type="button"
                className="btn btn-sm btn-primary report-backup-btn"
                onClick={handleBackup}
                disabled={backupLoading || filteredData.length === 0}
              >
                {backupLoading ? (
                  <span className="spinner-border spinner-border-sm me-2" role="status" />
                ) : (
                  <i className="bi bi-cloud-download me-1" />
                )}
                Fazer Backup em PDF
              </button>
            </div>
          </div>

          <DataTable
            columns={columns}
            data={filteredData}
            progressPending={loading}
            pagination
            paginationResetDefaultPage={resetPaginationToggle}
            highlightOnHover
            pointerOnHover
            responsive
            noHeader
            noDataComponent={noDataComponent}
            paginationComponentOptions={{
              rowsPerPageText: "Linhas",
              rangeSeparatorText: "de",
              selectAllRowsItem: true,
              selectAllRowsItemText: "Todos",
            }}
          />
        </section>
      </div>

      <div
        className="modal fade"
        id="dtemittionModal"
        tabIndex={-1}
        aria-labelledby="modalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div
              className="modal-header"
              style={{ background: "linear-gradient(135deg, #0d6efd, #4f8cff)" }}
            >
              <h5 className="modal-title text-white" id="modalLabel">
                <i className="bi bi-calendar-event me-2" />
                Data de emissão do relatório
              </h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              <div className="report-modal-note">
                Defina a data que será exibida no documento para este funcionário.
              </div>
              <label htmlFor="dtemittion" className="form-label">
                Escolha a data de emissão
              </label>
              <input
                type="date"
                id="dtemittion"
                className="form-control"
                value={selectedDate}
                onChange={(event) => setSelectedDate(event.target.value)}
              />
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                Cancelar
              </button>
              <button type="button" className="btn btn-primary" onClick={handleSaveDtemittion}>
                Salvar data
              </button>
            </div>
          </div>
        </div>
      </div>

      {showPreviewModal && (
        <>
          <div className="modal-backdrop fade show" onClick={closePreviewModal} />
          <div className="modal fade show d-block" tabIndex={-1} aria-hidden="true">
            <div className="modal-dialog modal-xl modal-dialog-centered">
              <div className="modal-content">
                <div
                  className="modal-header"
                  style={{ background: "linear-gradient(135deg, #0d6efd, #4f8cff)" }}
                >
                  <div>
                    <h5 className="modal-title text-white mb-1">
                      <i className="bi bi-eye me-2" />
                      Pré-visualizar PDF
                    </h5>
                    <small className="text-white-50">
                      Confira o documento antes de fazer o download.
                    </small>
                  </div>
                  <button
                    type="button"
                    className="mt-4 mr-4 btn-close btn-close-white"
                    onClick={closePreviewModal}
                  />
                </div>
                <div className="modal-body p-0 report-preview-modal__body">
                  {previewUrl ? (
                    <iframe
                      src={previewUrl}
                      title="Pré-visualização do relatório em PDF"
                      width="100%"
                      height="100%"
                      style={{ border: "none" }}
                    />
                  ) : (
                    <div className="report-preview-loading">
                      <div className="spinner-border text-primary" role="status" />
                      <p className="mb-0">Carregando PDF...</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {backupFailures.length > 0 && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setBackupFailures([])} />
          <div className="modal fade show d-block" tabIndex={-1} aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="bi bi-exclamation-triangle text-warning me-2" />
                    Itens que falharam no backup
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setBackupFailures([])}
                  />
                </div>
                <div className="modal-body">
                  <p>Os demais itens foram incluídos normalmente no arquivo baixado.</p>
                  <ul className="list-group">
                    {backupFailures.map((failure, index) => (
                      <li key={index} className="list-group-item">
                        <strong>{failure.label}</strong>: {failure.reason}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setBackupFailures([])}
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </LayoutSubmaster>
  );
}

export default ConfigReportDocument;
